import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { MessageCircle, X, Send, Sparkles, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SESSION_KEY = "good_fruit_chat_session";
const MESSAGES_KEY = "good_fruit_chat_messages";

// Lightweight markdown renderer for links + bold + line breaks
const renderMarkdown = (text) => {
  if (!text) return null;
  const parts = [];
  // Combined regex for [text](url) and **bold**
  const tokenRegex = /\[([^\]]+)\]\(([^)]+)\)|\*\*([^*]+)\*\*|\*([^*]+)\*/g;
  let lastIndex = 0;
  let match;
  let idx = 0;
  while ((match = tokenRegex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }
    if (match[1] && match[2]) {
      // Link
      const url = match[2];
      const isExternal = /^https?:\/\//i.test(url);
      parts.push(
        <a
          key={`l-${idx++}`}
          href={url}
          target={isExternal ? "_blank" : "_self"}
          rel={isExternal ? "noopener noreferrer" : undefined}
          className="text-teal-300 underline underline-offset-2 hover:text-teal-200"
        >
          {match[1]}
        </a>
      );
    } else if (match[3]) {
      parts.push(
        <strong key={`b-${idx++}`} className="font-semibold">
          {match[3]}
        </strong>
      );
    } else if (match[4]) {
      parts.push(
        <em key={`i-${idx++}`} className="italic">
          {match[4]}
        </em>
      );
    }
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < text.length) parts.push(text.slice(lastIndex));
  // Convert any remaining string parts to honor newlines
  return parts.map((p, i) =>
    typeof p === "string" ? (
      <React.Fragment key={`t-${i}`}>
        {p.split("\n").map((line, j, arr) => (
          <React.Fragment key={`tl-${j}`}>
            {line}
            {j < arr.length - 1 && <br />}
          </React.Fragment>
        ))}
      </React.Fragment>
    ) : (
      p
    )
  );
};

const greeting = {
  role: "assistant",
  content:
    "Hi, I'm Melanie — Min. Tyree's assistant. I can help you find your way around Good Fruit, the finance course, funding paths, or our Credit Mastery membership. What can I help you with today?",
};

const QUICK_PROMPTS = [
  "Tell me about the finance course",
  "How does Leap of Faith Funding work?",
  "What's included in Credit Mastery?",
  "Who is Min. Tyree?",
];

const MelanieChat = () => {
  const [open, setOpen] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([greeting]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  // Restore session
  useEffect(() => {
    try {
      const sid = localStorage.getItem(SESSION_KEY);
      const stored = localStorage.getItem(MESSAGES_KEY);
      if (sid) setSessionId(sid);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed) && parsed.length) setMessages(parsed);
      }
    } catch (e) {
      // ignore
    }
  }, []);

  // Persist messages
  useEffect(() => {
    try {
      localStorage.setItem(MESSAGES_KEY, JSON.stringify(messages.slice(-50)));
    } catch (e) {
      // ignore
    }
  }, [messages]);

  // Persist sessionId
  useEffect(() => {
    if (sessionId) localStorage.setItem(SESSION_KEY, sessionId);
  }, [sessionId]);

  // Autoscroll
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, open, sending]);

  // Focus input on open
  useEffect(() => {
    if (open) {
      const t = setTimeout(() => inputRef.current?.focus(), 200);
      return () => clearTimeout(t);
    }
  }, [open]);

  const sendMessage = async (text) => {
    const trimmed = (text || "").trim();
    if (!trimmed || sending) return;
    setError(null);
    const userMsg = { role: "user", content: trimmed };
    setMessages((prev) => [...prev, userMsg]);
    setInput("");
    setSending(true);
    try {
      const res = await axios.post(`${API}/chat/send`, {
        session_id: sessionId,
        message: trimmed,
      });
      if (res.data?.session_id && !sessionId) setSessionId(res.data.session_id);
      const reply = res.data?.reply || "I'm sorry, something went wrong.";
      setMessages((prev) => [...prev, { role: "assistant", content: reply }]);
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || "Network error";
      setError(detail);
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content:
            "I had trouble connecting just now. Please try again in a moment — and if it keeps happening, you can reach out via the contact link in the footer.",
        },
      ]);
    } finally {
      setSending(false);
    }
  };

  const onSubmit = (e) => {
    e.preventDefault();
    sendMessage(input);
  };

  const reset = () => {
    setMessages([greeting]);
    setSessionId(null);
    localStorage.removeItem(SESSION_KEY);
    localStorage.removeItem(MESSAGES_KEY);
  };

  return (
    <>
      {/* Floating button */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          aria-label="Open Melanie chat"
          className="fixed bottom-20 right-5 z-[60] group inline-flex items-center gap-2 bg-teal-500 hover:bg-teal-400 text-black font-semibold rounded-full pl-4 pr-5 py-3 shadow-[0_15px_40px_-10px_rgba(20,184,166,0.6)] transition-all hover:-translate-y-0.5"
        >
          <span className="relative inline-flex items-center justify-center w-8 h-8 rounded-full bg-black text-teal-300">
            <MessageCircle className="w-4 h-4" />
            <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-emerald-400 ring-2 ring-teal-500 group-hover:ring-teal-400" />
          </span>
          <span className="text-sm">Chat with Melanie</span>
        </button>
      )}

      {/* Chat panel */}
      {open && (
        <div className="fixed bottom-20 right-5 z-[60] w-[calc(100vw-2.5rem)] sm:w-[400px] h-[min(620px,calc(100vh-6rem))] bg-[#04181a] text-white rounded-2xl border border-teal-400/30 shadow-[0_25px_60px_-15px_rgba(0,0,0,0.7)] flex flex-col overflow-hidden">
          {/* Header */}
          <div className="relative px-5 py-4 border-b border-white/10 bg-gradient-to-r from-black to-[#04181a]">
            <div
              className="absolute inset-0 opacity-20"
              style={{
                backgroundImage:
                  "radial-gradient(circle at 30% 50%, #14b8a6 0%, transparent 60%)",
              }}
            />
            <div className="relative flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="relative w-10 h-10 rounded-full bg-teal-500 text-black flex items-center justify-center">
                  <Sparkles className="w-5 h-5" strokeWidth={2.25} />
                  <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-emerald-400 ring-2 ring-[#04181a]" />
                </div>
                <div>
                  <div className="text-white font-semibold tracking-tight leading-tight">
                    Melanie
                  </div>
                  <div className="text-[11px] text-teal-300/90">
                    Min. Tyree&apos;s Assistant · Online
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={reset}
                  title="Start a new conversation"
                  className="text-[11px] uppercase tracking-wide text-white/55 hover:text-teal-300 px-2 py-1 transition-colors"
                >
                  New
                </button>
                <button
                  onClick={() => setOpen(false)}
                  aria-label="Close chat"
                  className="text-white/60 hover:text-white p-1.5 rounded-md hover:bg-white/10 transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>

          {/* Messages */}
          <div
            ref={scrollRef}
            className="flex-1 overflow-y-auto px-4 py-5 space-y-3 scroll-smooth"
            style={{ scrollbarWidth: "thin" }}
          >
            {messages.map((m, i) => {
              const isUser = m.role === "user";
              return (
                <div
                  key={i}
                  className={`flex ${isUser ? "justify-end" : "justify-start"}`}
                >
                  <div
                    className={`max-w-[85%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed ${
                      isUser
                        ? "bg-teal-400 text-black rounded-br-md whitespace-pre-wrap"
                        : "bg-white/[0.06] text-white border border-white/10 rounded-bl-md"
                    }`}
                  >
                    {isUser ? m.content : renderMarkdown(m.content)}
                  </div>
                </div>
              );
            })}
            {sending && (
              <div className="flex justify-start">
                <div className="max-w-[85%] px-4 py-2.5 rounded-2xl text-sm bg-white/[0.06] border border-white/10 rounded-bl-md inline-flex items-center gap-2 text-white/70">
                  <Loader2 className="w-3.5 h-3.5 animate-spin text-teal-300" />
                  Melanie is typing…
                </div>
              </div>
            )}

            {/* Quick prompts after greeting only */}
            {messages.length === 1 && !sending && (
              <div className="pt-2 flex flex-wrap gap-2">
                {QUICK_PROMPTS.map((q) => (
                  <button
                    key={q}
                    onClick={() => sendMessage(q)}
                    className="text-xs text-teal-200 border border-teal-400/30 bg-teal-500/10 hover:bg-teal-500/20 hover:border-teal-400/50 rounded-full px-3 py-1.5 transition-colors"
                  >
                    {q}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Input */}
          <form
            onSubmit={onSubmit}
            className="border-t border-white/10 bg-black/40 backdrop-blur-sm p-3"
          >
            <div className="flex items-end gap-2">
              <textarea
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    sendMessage(input);
                  }
                }}
                placeholder="Ask Melanie a question…"
                rows={1}
                className="flex-1 resize-none bg-white/[0.05] border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white placeholder:text-white/35 focus:outline-none focus:ring-2 focus:ring-teal-400 focus:border-teal-400 max-h-24"
              />
              <button
                type="submit"
                disabled={!input.trim() || sending}
                className="shrink-0 w-10 h-10 rounded-xl bg-teal-400 hover:bg-teal-300 disabled:bg-white/10 disabled:text-white/30 text-black flex items-center justify-center transition-colors"
                aria-label="Send"
              >
                <Send className="w-4 h-4" />
              </button>
            </div>
            <div className="mt-2 text-[10px] text-white/35 text-center">
              Powered by Claude · Faith-aware · Not personal financial advice
            </div>
          </form>

          {error && (
            <div className="absolute top-16 inset-x-3 text-[11px] text-rose-300 bg-rose-900/40 border border-rose-400/30 rounded-md px-3 py-1.5 text-center">
              {String(error).slice(0, 120)}
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default MelanieChat;
